import { format } from "date-fns";
import { AlertCircle, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useSearchParams } from "react-router-dom";

import { useIsSuperAdmin } from "@/api/is-superadmin";
import { DateRangeFilter } from "@/components/daterange-filter";
import { FacetedFilter } from "@/components/faceted-filter";
import { SearchBar } from "@/components/search-bar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { TabNavigation } from "@/components/ui/tab-navigation";
import { useFetchRecordingTags } from "@/features/recording-tags/api/fetch-recording-tags";
import { useInfiniteRecordings } from "@/features/recordings/api/fetch-recordings";
import { NoRecordings } from "@/features/recordings/components/no-recordings";
import { RecordingItem } from "@/features/recordings/components/recording-item";
import { RecordingItemSkeleton } from "@/features/recordings/components/recording-item-skeleton";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { CanAddToPlaylist, RoleEnum } from "@classview/shared-types";

export function RecordingsPage() {
  const { organisation, hasPermission } = useOrganisation();
  const { isSuperAdmin } = useIsSuperAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState<string | null>(null);
  const [date, setDate] = useState<DateRange | undefined>(undefined);
  const [tags, setTags] = useState<string[]>([]);

  useEffect(() => {
    const initialTags = searchParams.get("tags")?.split(",") ?? [];

    if (initialTags.length > 0) {
      setTags(initialTags);
      setSearchParams((prev) => {
        prev.delete("tags");
        return prev;
      });
    }
  }, [searchParams, setSearchParams]);

  const {
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    isSuccess,
    data,
  } = useInfiniteRecordings({
    organisationId: organisation.id,
    pageSize: 9,
    search: search ?? "",
    startDate: date && date.from ? format(date.from, "yyyy-MM-dd") : undefined,
    endDate: date && date.to ? format(date.to, "yyyy-MM-dd") : undefined,
    tags: tags.length > 0 ? tags : undefined,
  });
  const { data: tagsData, isSuccess: isTagsSuccess } = useFetchRecordingTags({
    organisationId: organisation.id,
  });

  const tagOptions = isTagsSuccess
    ? tagsData.map((tag) => ({
        label: tag.name,
        value: tag.id,
      }))
    : [];

  const navigationItems = [
    { label: "All Recordings", to: "/recordings/all" },
    { label: "Playlists", to: "/recordings/playlists" },
    ...(isSuperAdmin
      ? [
          {
            label: "Storage",
            to: "/recordings/storage",
          },
        ]
      : []),
  ];

  const hasPlaylistPermission = hasPermission(CanAddToPlaylist);
  const hasTagsPermission = hasPermission([
    RoleEnum.REPLAY,
    RoleEnum.REPLAY_ADVANCED,
  ]);

  if (isLoading)
    return (
      <section>
        <OnboardingHeader
          title="Replay"
          description="View your organisations recordings"
          className="mb-6"
        />
        {hasPlaylistPermission && (
          <TabNavigation className="mb-6" options={navigationItems} />
        )}
        <div className="mb-12 flex flex-col gap-1 md:flex-row">
          <div className="w-full md:max-w-[320px]">
            <SearchBar search={search} setSearch={setSearch} />
          </div>
          <div className="flex gap-1">
            {hasTagsPermission && (
              <FacetedFilter
                filter={tags}
                setFilter={setTags}
                title="Tags"
                options={tagOptions}
              />
            )}
            <DateRangeFilter date={date} setDate={setDate} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <RecordingItemSkeleton />
          <RecordingItemSkeleton />
          <RecordingItemSkeleton />
        </div>
      </section>
    );

  if (isError || !isSuccess)
    return (
      <section>
        <OnboardingHeader
          title="Replay"
          description="View your organisations recordings"
          className="mb-6"
        />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message ?? "An unexpected error occurred"}
          </AlertDescription>
        </Alert>
      </section>
    );

  const recordings = data.pages.flatMap((page) => page.items);
  return (
    <section>
      <OnboardingHeader
        title="Replay"
        description="View your organisations recordings"
        className="mb-6"
      />
      {hasPlaylistPermission && (
        <TabNavigation className="mb-6" options={navigationItems} />
      )}
      <div className="mb-12 flex flex-col gap-1 md:flex-row">
        <div className="w-full md:max-w-[320px]">
          <SearchBar search={search} setSearch={setSearch} />
        </div>
        <div className="flex gap-1">
          {hasTagsPermission && (
            <FacetedFilter
              filter={tags}
              setFilter={setTags}
              title="Tags"
              options={tagOptions}
            />
          )}
          <DateRangeFilter date={date} setDate={setDate} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {recordings.map((recording) => (
          <RecordingItem key={recording.id} recording={recording} />
        ))}
      </div>

      {recordings.length === 0 && <NoRecordings />}

      <div className="flex justify-center">
        {hasNextPage && (
          <Button
            onClick={() => fetchNextPage()}
            type="button"
            disabled={isFetchingNextPage}
            isLoading={isFetchingNextPage}
            isAnimated={isFetchingNextPage}
            variant="link"
          >
            {isFetchingNextPage ? (
              "Loading..."
            ) : (
              <>
                <ChevronDown className="mr-2 size-4" />
                Load More
              </>
            )}
          </Button>
        )}
      </div>
    </section>
  );
}
